import {
  setSessionExpiredCallback,
  notifySessionRestored,
} from 'helpers/apiRequest';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import SessionExpiredModal from '../SessionExpiredModal/SessionExpiredModal';

interface SessionManagerProps {
  children: React.ReactNode;
}

const SessionManager: React.FC<SessionManagerProps> = ({
  children,
}) => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    setSessionExpiredCallback(() => {
      setShowSessionModal(true);
    });
  }, []);

  // Proactive check when switching back to this tab/window
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('userToken');
        if (!token) return;
        const lastActive = parseInt(
          localStorage.getItem('lastActiveTime') || '0',
          10,
        );
        // If user was away for > 7 minutes (5 min inactivity + 2 min warning grace)
        if (lastActive && Date.now() - lastActive > 1000 * 60 * 7) {
          setShowSessionModal(true);
        }
      }
    };

    const updateLastActive = () => {
      localStorage.setItem('lastActiveTime', Date.now().toString());
    };

    window.addEventListener('mousemove', updateLastActive);
    window.addEventListener('keydown', updateLastActive);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('mousemove', updateLastActive);
      window.removeEventListener('keydown', updateLastActive);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRefreshToken');
    localStorage.removeItem('userData');
    setShowSessionModal(false);
    navigate('/login', { replace: true });
  };

  const handleSessionRestored = () => {
    localStorage.setItem('lastActiveTime', Date.now().toString());
    notifySessionRestored();
    setShowSessionModal(false);

    // Cancel in-flight queries before invalidating cache with fresh token
    queryClient.cancelQueries();
    queryClient.invalidateQueries();

    // Reload the page to fetch fresh data across all tables, accounts and services
    window.location.reload();
  };

  return (
    <>
      {children}
      <SessionExpiredModal
        isVisible={showSessionModal}
        onLogout={handleLogout}
        onSessionRestored={handleSessionRestored}
      />
    </>
  );
};

export default SessionManager;
